import { Gizmo, RootGizmo } from './gizmos';

export type TGizmoType =
  /* ROOT GIZMOS */
  | 'form'
  | 'view'
  /* VISUAL */
  | 'tag'
  | 'button'
  | 'text'
  | 'index'
  /* CONTAINERS */
  | 'row'
  | 'column'
  | 'tabs'
  | 'tab'
  | 'section'
  /* FIELDS */
  | 'text_field'
  | 'number_field'
  | 'select_field'
  | 'date_field'
  | 'file_field'
  | 'phone_field'
  | 'multimedia_field'
  | 'signature_field'
  | 'rate_field'
  | 'boolean_field'
  | 'location_field'
  | 'id_ocr_field'
  | 'annex_form'
  /* ANALYTIC */
  | 'chart'
  | 'indicator'
  /* CUSTOM */
  | 'custom'
  /* NAVIGATION */
  | 'route'
  /* MENU */
  | 'menu'
  | 'sub_menu'
  /* HEADER */
  | 'navbar_menu'
  | 'navbar_sub_menu'
  | 'header_menu'
  | 'header_sub_menu'
  /* DISPLAY */
  | 'folio'
  /* LEGACY */
  | 'legacy_invalid';

export interface IFlowyProject {
  fid: string;
  type: 'project';
  label: string;
  navigation?: {
    routes: IGizmo[];
  };
  navbar?: {
    gizmos: IGizmo[];
  };
  sidebar?: {
    gizmos: IGizmo[];
  };
  views?: {
    gizmos: IGizmo[];
  };
  forms?: {
    gizmos: IGizmo[];
  };
}

/**
 * This is the data that will be sent to the platform layer in order to render
 * the Routes
 */
export interface IFlowyRoute {
  path: string;
  access?: 'public' | 'protected';
  configs?: IGizmo[];
}

// export interface IRoute {
//   fid: string;
//   type: 'route';
//   label: string;
//   uid?:
// }

export interface IGizmo {
  /**
   * Flowy id
   */
  fid: string;
  /**
   * User id, this id will be generated automatically by Flowy depending on the
   * label value. This id has the particularity that it can be updated by the user
   * who's configuring the platform.
   */
  uid?: string;
  /**
   * Gizmo type
   */
  type: TGizmoType;
  /**
   * Gizmo's label
   */
  label: string;

  /**
   * This configuration is inherited from Flowy 2, we're conserving it because we're working right
   * now on Flowy 2 mobile app
   * TODO: remove this?
   */
  number?: string;

  /**
   * Optional field given to the user in order to add a comment that will later help them
   */
  description?: string;

  preGizmos?: IGizmo[];
  gizmos?: IGizmo[];
  postGizmos?: IGizmo[];

  // This will be used in case we're configuring a 'page' view
  navbarGizmos?: IGizmo[];
  sidebarGizmos?: IGizmo[];

  version?: number;

  ops?: IGizmoOptions;

  catalogs?: any[];

  /**
   * V2 gizmos
   */
  questions?: IGizmoQuestionV2[];
  /**
   * Form statuses
   */
  status?: IRootGizmoDataStatus[];
}

export interface IGizmoOptions {
  /**
   * If the Gizmo is required, default false.
   */
  required?: {
    /**
     * True if the Gizmo data is required
     */
    isRequired: boolean;
    flux?: string;
    /**
     * If the Gizmo has no data this message will be displayed instead of the default
     */
    errorMessage?: string;
  };
  /**
   * If set to false (or if flux evals to false) the question won't be shown
   */
  display?: {
    isDisplayed: boolean;
    flux?: string;
  };
  /**
   * If set to false (or if flux evals to false) the question won't be editable
   */
  editable?: {
    isEditable: boolean;
    flux?: string;
  };
  value?: {
    /**
     * Right now we're only going to support to get the value during initialization
     */
    flux: string;
    /**
     * If an aggregation is configured this will filter the results
     */
    filter?: string;
    /**
     * Only when this condition is met the value will be updated
     */
    condition?: string;
    // TODO: should we remove this?
    moment?: 'init' | 'empty' | 'filled' | 'init_and_empty' | 'always';
  };
  /**
   * When configured a help message will be shown on the gizmo
   */
  help?: {
    message: string;
    /**
     * Types of help messages
     *  - tooltip: a tooltip will be shown
     *  - text: a text will be shown
     */
    type?: 'tooltip' | 'text';
    position?: THelpPosition;
  };
  /**
   * In case it's configured this Flux script is going to be run locally
   */
  localValidation?: ILocalValidation;
  /**
   * In case it's configured this Flux script is going to be run on the server
   */
  serverValidation?: IServerValidation;
  /**
   * In case it's configured this Flux script is going to be run on the server
   */
  serverScript?: IServerScript;
  /**
   * When set to false the Gizmo won't show the label, if not set the default behaviour is to
   * display the label
   */
  displayLabel?: boolean;

  /**
   * In case it's configured the data will be set on the configured path
   */
  json?: IJsonOptions;
  /**
   * When the gizmo is configured to be displayed
   */
  indexColumn?: {
    /**
     * It shows as column on the index
     */
    isDisplayed?: boolean;
    /**
     * When configured it will be shown instead of the gizmo's label
     */
    label?: string;
    /**
     * Column search options
     */
    search?: {
      isEnabled?: boolean;
    };
  };
  search?: ISearchOptions;

  chart?: {
    type: 'bar' | 'area' | 'line' | 'pie';
    /**
     * RootGizmo fid of the form that will be used to get the data
     */
    rootGizmoFid?: string;
    /**
     * When it's defined the rootGizmoFid is going to be taken from the params, we're going to use
     * the specified field
     */
    paramRootGizmoFid?: string;
  };
  row?: IRowOptions;
  section?: ISectionOptions;
  form?: IFormOptions;
  indicator?: IIndicatorOptions;
  button?: IButtonOptions;
  textField?: ITextFieldOptions;
  fileField?: IFileFieldOptions;
  multimediaField?: IMultimediaFieldOptions;
  idOcrField?: IIdOcrFieldOptions;
  custom?: ICustomOptions;
  index?: IIndexOptions;
  route?: IRouteOptions;
  text?: ITextOptions;
  numberField?: INumberFieldOptions;
  selectField?: ISelectFieldOptions;
  dateField?: IDateFieldOptions;
  phoneField?: IPhoneFieldOptions;
  annexForm?: IAnnexFormOptions;
  folio?: IFolioOptions;
  rateField?: IRateFieldOptions;
}

/**
 * Object containing the configured columns for the 4 defined sizes
 */
export type TColumns = {
  xl: 1 | 2 | 3 | 4 | 5 | 6;
  l: 1 | 2 | 3 | 4;
  m: 1 | 2 | 3 | 4;
  s: 1 | 2;
};

interface IJsonOptions {
  path: string;
  /**
   * We're going to specify this in case there is confusion
   */
  // type?: 'object' | 'array';
}

interface IFormOptions {
  /**
   * When the form has this configured it will be used to authenticate the client
   * user (which is different from the Flowy users)
   */
  auth?: {
    endpoint?: 'login' | 'signup';
    /**
     * When set to true the user will be logged in after signing up
     */
    loginAfterSignup?: boolean;
    rootGizmoFid?: string;
    usernameGizmoUid?: string;
    emailGizmoUid?: string;
    phoneGizmoUid?: string;
  };
  /**
   * When configured this string will be shown in the save button
   */
  saveButtonLabel?: string;
  /**
   * When configured this string will be shown in the cancel button
   */
  cancelButtonLabel?: string;
  /**
   * When set to false the form won't show the cancel button, by default it will
   * show it
   */
  displayCancelButton?: {
    isDisplayed: boolean;
    flux?: string;
  };
  onlineCatalogs?: boolean;
  /**
   * Configuration that will affect the way the registers index is going to
   * function
   */
  registersIndex?: {
    /**
     * Status column configuration
     */
    statusColumn?: {
      display?: {
        /**
         * When set to true the status column will be shown
         */
        isDisplayed: boolean;
      };
    };
    createButton?: {
      /**
       * Set the create button label
       */
      label?: string;
    };
  };
  analytics?: {
    create?: boolean;
    edit?: boolean;
    save?: boolean;
    sections?: {
      forward?: boolean;
      back?: boolean;
    };
  };
}

interface ISearchOptions {
  /**
   * Minimal characters that should be present before triggering a search
   */
  minChars?: number;
  /**
   * It will be configured when we're trying to search on another root gizmo
   */
  rootGizmoFid?: string;
  /**
   * Flux expression that will be interpolated and sent to the server
   */
  fluxSearch?: string;
  /**
   * Text that will be searched on the server
   */
  textSearch?: string;
  /**
   * Configuration to search on configured columns
   */
  columnSearch?: {
    logicOperator?: 'and' | 'or';
    columns: {
      /**
       * When both are not configured we're going to use the current gizmo value
       */
      valueGizmoUid?: string;
      valueGizmoFid?: string;
      /**
       * Fid of the column where we're going to search
       */
      columnGizmoFid?: string;
    }[];
  };
  /**
   * It works same as in form options, the configured columns will be used to create the value
   * that will be displayed in the UI
   */
  columns?: IGizmoQuestionV2[];
  /**
   * Since the result will be 'modeled' after the catalog options, part of this is the
   * possitiblity to add the metadata value
   */
  metadata?: IGizmoQuestionV2[];
  /**
   * If this is configured, when the user clicks on a search result we're going to check if the
   * status of the search result is valid, if it's not valid we're going to show the error.
   */
  searchResultStatus?: {
    status?: string;
    flux?: string;
    valid: boolean;
    errorMessage: string;
    /**
     * When set to true and the search result has associations field we're going
     * to show a button to navigate to that associated register
     */
    showAssociations?: boolean;
  }[];
  /**
   * When set to true the search result will be selected automatically if it's only one
   */
  searchResultSelectSingleResult?: boolean;
  /**
   * When user selects a search result and it doesn't match any error status or flux, this
   * message will be shown
   */
  searchResultSelectedMessage?: string;
}

interface IRowOptions {
  /**
   * Number of columns that will be inside the row
   */
  columns: TColumns;
  /**
   * How the columns are going to be aligned
   */
  justify?: 'start' | 'center' | 'end' | 'space-between' | 'space-around';
}

interface ISectionOptions {
  jumps: {
    /**
     * Destiny section user id
     */
    destinyUid?: string;
    /**
     * Destiny section Flowy id
     */
    destinyFid?: string;
    /**
     * The gizmo that was configured on v2
     */
    destinyGizmoFid: string;
    flux: string;
  }[];
}

interface IIndicatorOptions {
  rootGizmoFid?: string;
  /**
   * When it's defined the rootGizmoFid is going to be taken from the params, we're going to use
   * the specified field
   */
  paramRootGizmoFid?: string;
}

interface IRateFieldOptions {
  /**
   * Rate field control type
   */
  type: 'face' | 'color_face' | 'stars' | 'thumbs';
  /**
   * Number of rate levels to show
   */
  quantity: number;
}

export type TButtonOperation =
  | 'form.submit'
  | 'form.cancel'
  | 'navigation.link'
  | 'section.jump'
  | 'section.next'
  | 'section.back'
  | 'auth.logout'
  | 'download';

interface IButtonOptions {
  /**
   * Button operation
   */
  // operation: 'form.submit' | 'navigation.link';
  operation: TButtonOperation;
  /**
   * If the button is configured as navigation.link
   */
  navigation?: {
    url?: string;
    routeFid?: string;
    path?: string;
  };
  /**
   * If the button is configured to download
   */
  download?: {
    url?: string;
    filename?: string;
    method?: 'GET' | 'POST';
  };
  /**
   * Show confirmation dialog before executing the button operation
   */
  confirmation?: {
    title: string;
    description: string;
  };
  /**
   * When set to false the label won't be shown in the button
   */
  displayLabel?: boolean;
  /**
   * Button's icon configuration
   */
  icon?: {
    name?: string;
  };
}

interface ITextFieldOptions {
  /**
   * Regex validations, when set to 'custom' developer will set the regex and message
   */
  regex?: {
    /**
     * Type of regex that will be validated
     */
    type: 'custom' | 'email' | 'rfc' | 'curp';
    /**
     * Custom regex which the developer will set
     */
    customRegex?: string;
    /**
     * Message that will be shown when the regex is not fulfilled
     */
    errorMessage?: string;
  };
  password?: {
    /**
     * If a text question is configured as password it will be shown as hidden
     */
    isPassword?: boolean;
    /**
     * When set to true and the password is set to true it will show a control that
     * when clicked it will display the password openly
     */
    showControl?: boolean;
  };
  /**
   * Maximum number of characters accepted as a value
   */
  minLength?: number;
  /**
   * Minimum number of characters accepted as a value
   */
  maxLength?: number;
  maxWords?: number; //! SHOULD THIS TWO GO?
  minWords?: number;
  /**
   * Number of lines the text area will have height
   */
  numberOfLines?: number;
  /**
   * Text that will be displayed before the user fills anything, i.e.,
   * we have no value
   */
  placeholder?: string;
  /**
   * Show the user a character counter
   */
  showCharacterCounter?: boolean;
  /**
   * Show the user a word counter (characters separated by space)
   */
  showWordCounter?: boolean;
  /**
   * Text to be shown before the value the user is introducing
   */
  prefix?: string;
  /**
   * Text to be shown after the value the user is introducing
   */
  suffix?: string;
  /**
   * If set to true all of the text introduced by the user will be transformed
   * to uppercase
   */
  // isUpperCase?: boolean;
  toUppercase?: boolean;
  /**
   * When configured as numeric only numbers will be admitted in the text_field
   */
  keyboard?: 'numeric';
  /**
   * When set to true the text will be trimmed
   */
  trim?: boolean;
  /**
   * When set to true the button to show the code scanner will be shown
   */
  codeScannerEnabled?: boolean;
  codeScanner?: {
    // TODO: migrate 'codeScannerEnabled' to this
    enabled?: boolean;
    /**
     * When enabled the read data will be processed to extract the curp (in case it's a curp)
     */
    processCurp?: boolean;
    curp?: {
      curpGizmoFid?: string;
      nameGizmoFid?: string;
      lastNameGizmoFid?: string;
      secondLastNameGizmoFid?: string;
      sexGizmoFid?: string;
      birthDateGizmoFid?: string;
      entityGizmoFid?: string;
      entityCodeGizmoFid?: string;
    };
  };
}

interface ICustomOptions {
  customParams?: any;
  /**
   * Custom Gizmo
   */
  component: any;
}

/**
 * Configuration for each operation that will be in the operations column
 */
export interface IIndexOperation {
  label: string;
  type:
    | 'form.index'
    | 'form.stats'
    | 'register.edit'
    | 'register.delete'
    | 'register.show'
    | 'register.pdf';
  path?: string;
}

interface IIndexOptions {
  type?: 'table' | 'map' | 'kanban';
  rowType: 'forms' | 'root-gizmo-data' | 'form.registers';
  registers?: {
    showStatus?: boolean;
    showCreate?: boolean;
    createLabel?: string;
  };
  operations?: IIndexOperation[];
  navigation?: {
    /**
     * Depending on the type of index this path will be used to navigate
     */
    path?: string;
    /**
     * When a path is configured it means that we're going to navigate to a
     */
    rootGizmoPath?: string;
    /**
     * When a gizmo configuration is arriving in the column we're going to use this
     * path to navigate
     */
    gizmoPath?: string;
  };
}

interface IRouteOptions {
  /**
   * When set to true the specified root gizmo will be used as entry point
   */
  index: boolean;
  /**
   * Fid of the view the route is
   */
  rootGizmoFid: string;
  /**
   * If provided it will be the assigned path, if not provided the uid will be used, if not
   * provided a random one will be generated
   */
  path?: string;
  /**
   * When set to true it means that when trying to access a protected route we're going to
   * redirect to the route that has this flag set to true
   */
  // login?: boolean;
  // access?: 'public' | 'protected';
}

interface ITextOptions {
  type: 'default' | 'success' | 'warning' | 'secondary' | 'danger';
}

interface INumberFieldOptions {
  /**
   * Value has to be greater than this
   */
  minimum?: number;
  /**
   * Value has to be less than this
   */
  maximum?: number;
  /**
   * True if the number if going to have decimals
   */
  decimals?: boolean;
  /**
   * When decimals options is set to true we can set the maximuma number of decimals that will
   * be used when capturing
   */
  numberOfDecimals?: number;
  /**
   * When set to true the number will be displayed with commas
   */
  thousands?: boolean;
  /**
   * When set to true the field will allow negative numbers
   */
  negative?: boolean;
  /**
   * Text to be shown before the value the user is introducing
   */
  prefix?: string;
}

interface ISelectFieldOptions {
  /**
   * Catalog's fid
   */
  catalogFid: string;
  /**
   * In case it's an 'options_dependant', a select Gizmo that has a parent, this will be
   * populated
   */
  parentFieldFid?: string;
  // parentFieldUid?: string;
  /**
   * When set to true the user can select multiple options
   */
  isMultiple?: boolean;
  /**
   * When this is set the options will be taken from that Gizmo
   */
  gizmoUid?: string;
  /**
   * When this list is set the options will be gathered from the list of configured Gizmos
   */
  gizmoUids?: string[];
  /**
   * In case the info is comming from an external gizmo we're configuring how
   * the value will be calculated, it's a flux interpolation
   */
  value?: string;
  /**
   * If provided it will add the label key to the option (IGizmoCatalogOptionV2)
   */
  label?: string;
  /**
   * When present it will filter the calculated options, it's a flux expression
   */
  filter?: string;
  /**
   * When set to true an other option is added
   */
  other?: boolean;
  /**
   * This value is used by the UI layer, when it's present it will be shown to the user
   * instead of the default 'OTRO' message
   */
  otherMessage?: string;
  /**
   * When set to true the options will be shuffled before being shown to the user
   */
  randomOrder?: boolean;
  /**
   * Sort options by 'value' key, default is asc
   */
  sort?: 'asc' | 'desc' | 'none';
  /**
   * This will be populated in case the options build by filled forms have metadata on the
   * 'answers' key
   */
  metadata?: IGizmo[];
  /**
   * Maximum number of selected options
   */
  maximum?: number;
  /**
   * Minimum number of selected options
   */
  minimum?: number;
  /**
   * When set to true it means the gizmo data has an extra attribute 'checked' which means if
   * it was 'accepted' or 'rejected'
   */
  multipleTristate?: boolean;
  /**
   * Type of control to show on the UI layer
   */
  multipleControl?: string;
  controlType?: 'select' | 'radio' | 'checkbox';
  /**
   * When set to true the user will be able to search through the options
   */
  search?: boolean;
}

interface IDateFieldOptions {
  /**
   * The type of control that will be shown on the platform layer
   */
  mode?: 'date' | 'datetime' | 'time';
  /**
   * Values using keywords like 'today', right now only 'today' is supported
   */
  defaultValue?: string;
}

interface IAnnexFormOptions {
  /**
   * ID of the RootGizmo id of the child
   */
  rootGizmoFid: string;
  /**
   * Columns that that are displayed on the on the index
   */
  columns?: {
    /**
     * Flowy id of the column (which is the same as question f_id)
     */
    f_id: string;
    /**
     * Number of column
     */
    number: string;
    /**
     * Title of the column
     */
    title: string;
  }[];
  /**
   * Minimum acceptable child annex forms
   */
  min?: number;
  /**
   * Maximum acceptable child annex forms
   */
  max?: number;
  /**
   * If set to true the user can create a new annex form
   */
  createEnabled?: boolean;
  /**
   * When set it will be displayed as label on the create button
   */
  createButtonLabel?: string;
  /**
   * If set to true the user can search annex forms and associate them
   * TODO: check if this functionality will stay in this version
   */
  searchEnabled?: boolean;
}

interface IFileFieldOptions {
  // allowedExtensions?: ''[];
  /**
   * When configured the file will be send to server for processing, right now we're supporting the
   * extraction of CURP information from a pdf file.
   */
  serverProcessing?: {
    type: 'curp' | 'personal_id';
    /**
     * When the server processing is for a CURP file we can configure the gizmos that will
     * receive the information
     */
    curp?: {
      curpGizmoUid?: string;
      nameGizmoUid?: string;
      lastNameGizmoUid?: string;
      secondLastNameGizmoUid?: string;
      entityGizmoUid?: string;
      entityCodeGizmoUid?: string;
      birthDateGizmoUid?: string;
      sexGizmoUid?: string;
    };
    personalId?: {
      fullDocumentImageGizmoUid?: string;
      fullDocumentImageGizmoFid?: string;
      faceImageGizmoUid?: string;
      faceImageGizmoFid?: string;
      fullDocumentFrontImageGizmoUid?: string;
      fullDocumentFrontImageGizmoFid?: string;
      fullDocumentBackImageGizmoUid?: string;
      fullDocumentBackImageGizmoFid?: string;
      nameGizmoUid?: string;
      nameGizmoFid?: string;
      lastNameGizmoUid?: string;
      lastNameGizmoFid?: string;
      secondLastNameGizmoUid?: string;
      secondLastNameGizmoFid?: string;
      genderGizmoUid?: string;
      genderGizmoFid?: string;
      dateOfBirthGizmoUid?: string;
      dateOfBirthGizmoFid?: string;
      addressGizmoUid?: string;
      addressGizmoFid?: string;
      curpGizmoUid?: string;
      curpGizmoFid?: string;
      electoralSectionGizmoUid?: string;
      electoralSectionGizmoFid?: string;
      streetGizmoUid?: string;
      streetGizmoFid?: string;
      neighborhoodGizmoUid?: string;
      neighborhoodGizmoFid?: string;
      zipcodeGizmoUid?: string;
      zipcodeGizmoFid?: string;
      municipalityGizmoUid?: string;
      municipalityGizmoFid?: string;
      stateGizmoUid?: string;
      stateGizmoFid?: string;
      electoralStateGizmoUid?: string;
      electoralStateGizmoFid?: string;
      cicGizmoUid?: string;
      cicGizmoFid?: string;
      ocrGizmoUid?: string;
      ocrGizmoFid?: string;
      mrzGizmoUid?: string;
      mrzGizmoFid?: string;
      expiryYearGizmoUid?: string;
      expiryYearGizmoFid?: string;
      issueYearGizmoUid?: string;
      issueYearGizmoFid?: string;
      registrationYearGizmoUid?: string;
      registrationYearGizmoFid?: string;
      emissionNumberGizmoUid?: string;
      emissionNumberGizmoFid?: string;
    };
  };
  /**
   * Configuration that will be used in the UI layer to set which files are accepted
   */
  accept?: string;
  /**
   * When set to true it will use the downloadFileV2 endpoint
   */
  useApiV2?: boolean;
}

interface IMultimediaFieldOptions {
  /**
   * In case it's opened in a mobile device
   */
  defaultCamera?: 'front' | 'back';
  /**
   * From where the user an take the multimedia
   */
  inputSource?: 'both' | 'camera' | 'local_file';
  accept?: string;
}

interface IIdOcrFieldOptions {
  inputSource?: 'both' | 'camera' | 'local_file';
  /**
   * Set to false when after setting the images, they won't be processed in the server
   */
  processingEnabled?: boolean;
  /**
   * Gizmos that will receive the information extracted from the ocr
   */
  personalId?: {
    fullDocumentImageGizmoUid?: string;
    fullDocumentImageGizmoFid?: string;
    faceImageGizmoUid?: string;
    faceImageGizmoFid?: string;
    fullDocumentFrontImageGizmoUid?: string;
    fullDocumentFrontImageGizmoFid?: string;
    fullDocumentBackImageGizmoUid?: string;
    fullDocumentBackImageGizmoFid?: string;
    nameGizmoUid?: string;
    nameGizmoFid?: string;
    lastNameGizmoUid?: string;
    lastNameGizmoFid?: string;
    secondLastNameGizmoUid?: string;
    secondLastNameGizmoFid?: string;
    genderGizmoUid?: string;
    genderGizmoFid?: string;
    dateOfBirthGizmoUid?: string;
    dateOfBirthGizmoFid?: string;
    addressGizmoUid?: string;
    addressGizmoFid?: string;
    curpGizmoUid?: string;
    curpGizmoFid?: string;
    electoralSectionGizmoUid?: string;
    electoralSectionGizmoFid?: string;
    streetGizmoUid?: string;
    streetGizmoFid?: string;
    neighborhoodGizmoUid?: string;
    neighborhoodGizmoFid?: string;
    zipcodeGizmoUid?: string;
    zipcodeGizmoFid?: string;
    municipalityGizmoUid?: string;
    municipalityGizmoFid?: string;
    stateGizmoUid?: string;
    stateGizmoFid?: string;
    electoralStateGizmoUid?: string;
    electoralStateGizmoFid?: string;
    cicGizmoUid?: string;
    cicGizmoFid?: string;
    ocrGizmoUid?: string;
    ocrGizmoFid?: string;
    mrzGizmoUid?: string;
    mrzGizmoFid?: string;
    expiryYearGizmoUid?: string;
    expiryYearGizmoFid?: string;
    issueYearGizmoUid?: string;
    issueYearGizmoFid?: string;
    registrationYearGizmoUid?: string;
    registrationYearGizmoFid?: string;
    emissionNumberGizmoUid?: string;
    emissionNumberGizmoFid?: string;
  };
  personalIdOptions?: {
    /**
     * It will transform the entity from what's on the ID to the complete name
     */
    entityTransform?: boolean;
    /**
     * When set, the scanner will only ask for the front part of the id
     */
    onlyFront?: boolean;
  };
  /**
   * When set to false controls will be shown in the UI in order for the user to attach images
   */
  scannerEnabled?: {
    /**
     * Flag that will be used to determine if the scannerEnabled feature is enabled
     */
    isEnabled?: boolean;
    /**
     * When configured, this flux script will be evaluated to determine if the scanner
     * should be enabled
     */
    flux?: string;
  };
}

interface IPhoneFieldOptions {
  /**
   * When set to true the user will be sent a verification code to the phone number
   */
  verificationCode?: boolean;
  /**
   * Verification specific configurations
   */
  verification?: {
    /**
     * Right now we're sending the name of the person that's registering on the verification SMS,
     * in here we're setting the flux expression that will be used to get the name
     */
    nameFlux?: string;
    /**
     * Same here, we're also sending the username on the verification SMS
     */
    usernameFlux?: string;
  };
  /**
   * When set it will be used to configure the mask of the phone number
   */
  customMask?: string;
}

interface IFolioOptions {
  /**
   * Flux script that descrbies the folio
   */
  flux?: string;
  /**
   * When set to true the folio will be shown as a QR code
   */
  showFolioAsQr?: boolean;
}

/**
 * In case it's configured this Flux script is going to be run locally
 */
interface ILocalValidation {
  /**
   * If this is configured and the result is false this message will be shown
   */
  errorMessage?: string;
  /**
   * Flux expression that will be run locally
   */
  flux?: string;
}

/**
 * In case it's configured this Flux script is going to be run on the server
 */
interface IServerValidation {
  /**
   * If this is configured and the result is false this message will be shown
   */
  errorMessage?: string;
  /**
   * Flux expression that will be run on the server
   */
  flux?: string;
}

/**
 * When this is configured a flux script will be run on the server
 */
interface IServerScript {
  flux?: string;
  // lifecycle?: 'onCreate' | 'onUpdate' | 'onDelete';
  run?: {
    stage: 'onValidation' | 'onSetData';
    onSetData?: {
      /**
       * When the set data is being used and this regex is configured, it has to
       * fullfilled
       */
      regex?: string;
    };
  };
  /**
   * When the result arriving from the server is a register we're going to
   * set to which gizmos the data will be sent
   */
  registerResult?: {
    existsUid?: string;
    existsFid?: string;
    destinies?: {
      originFid?: string;
      originUid?: string;
      destinyFid?: string;
      destinyUid?: string;
    }[];
  };
}

export interface ILocationData {
  latitude: number;
  longitude: number;
  speed?: number;
  heading?: number;
  accuracy?: number;
  timestamp?: number;
}

export interface IGizmoData {
  /**
   * Gizmo's value
   */
  v: string | number | Date | ILocationData;
  fid?: string;
  /**
   * Selected option data
   */
  sod?: IGizmoCatalogOptionV2;
  /**
   * Chart data
   */
  cd?: { x: number | string; y: number }[];
  /**
   * File data
   */
  fd?: {
    attachmentId: string;
    fileName?: string;
    fileType?: string;
    fileSize?: number;
    fileExtension?: string;
  };
  /**
   * Id OCR data
   */
  id?: {
    attachmentId: string;
    source: 'web' | 'mobile';
    ocrResult?: any;
    fileName?: string;
    fileType?: string;
    fileSize?: number;
    fileExtension?: string;
    /**
     * When it's set to true it means the user has the image of the front side of the document
     * but not the back side
     */
    onlyFront?: boolean;
  };
  /**
   * Date data
   */
  dd?: {
    timestamp: number;
    timezone?: string;
  };
  /**
   * Annex form data
   */
  afd?: {
    // This is the fid
    filledFormId: string;
    id?: string;
    formId: string;
    columns?: {
      f_id: string;
      value: string;

      number?: string;
      title?: string;
    }[];
    editable: boolean;
  };
  /**
   * Search data
   */
  sd?: {
    filledFormId?: string;
    f_id?: string;
    formId?: string;
    answers?: any;
  };
  /**
   * Text field data
   */
  td?: {
    /**
     * There is a configuration that processes the string to extract the curp, the curp is shown
     * as the gizmo value. So the original string is saved here
     */
    scannedCurp?: string;
  };
}

/**
 * Params for the setData function implemented by the binders
 */
export interface IBinderSetDataParams {
  /**
   * Gizmo data that will be set
   */
  data: IGizmoData;
  /**
   * Gizmo event that triggered the setData function
   */
  event?: IGizmoEvent;
}

/**
 * Posible interfaces of a gizmo data field
 */
export type TGizmoData = IGizmoData | IGizmoData[] | undefined;

export type TJsonData = { [key: string]: any };

export interface IRootGizmoData {
  id?: string;
  /**
   * Filled form id
   */
  fid: string;
  /**
   * Filled form id, which is generated on the client
   * TODO: remove this
   */
  filledFormId?: string;
  /**
   * Version number of the filled form
   */
  version: number;
  /**
   * Flowy id of the RootGizmo used to produce this data
   */
  rootGizmoFid: string;
  /**
   * Version of the RootGizmo configuration used to produce this data
   */
  rootGizmoVersion: number;
  /**
   * Id of the form to which the filled form belongs to
   */
  formId?: string;
  /**
   * Version of the form we're using to create this filled form
   */
  formVersion?: number;

  //! Should this three go? They are implit in offline state
  // draft?: boolean;
  // uploaded?: boolean;
  // updated?: boolean;

  d?: {
    [qfid: string]: IGizmoData | IGizmoData[];
  };

  /**
   * JSON data, it's the original data that was used to create the filled form
   */
  j?: TJsonData;

  /**
   * Current root gizmo data status
   */
  status?: string;
  /**
   * This field is used in the backend to change the register status
   */
  action?: string;
  /**
   * Epoch timestamp of when the root gizmo data is being opened
   */
  epoch_start?: number;
  /**
   * Epoch timestamp of when the root gizmo data is being saved
   */
  epoch_end?: number;
  /**
   * Filled depending on the platform being used
   */
  source?: 'web' | 'mobile';
  /**
   * The username of the user that created the root gizmo data
   */
  username?: string;
  /**
   * Server timestamp of when the root gizmo data was created
   */
  createdAt?: Date;

  offlineStatus?: TOfflineStatus;

  /**************** LEGACY ARE THEY STAYING? */
  isMainForm?: boolean;
  belongsTo?:
    | {
        id?: string;
        formId?: string;
        filledFormId?: string;
        filledFormIndex?: number;
        answerId?: string;
        columns?: {
          f_id: string;
          number: string;
          title: string;
        }[];
      }
    | boolean;
}

export interface IGizmoDataV2 {
  [answerIndex: number]: {
    /**
     * This is the most common key used in the answers of a question
     */
    value?: any;
    label?: string;
  };
}

/**
 * Interface used by the core to control the code scanner in the UI
 */
export interface ICodeScanner {
  show: boolean;
  label: string;
}

/**
 * In this object we're going to be putting dynamically generated data which is
 * not GizmoData and which won't be included on the config in order to not confuse
 * the user
 */
export interface IGizmoFeatures {
  number?: string;
  /**
   * Gizmo's label
   */
  label: string;
  /**
   * Display or not the label flag
   */
  displayLabel: boolean;
  /**
   * Display or not the Gizmo
   */
  display: boolean;
  /**
   * Whether or not a Gizmo can be edited (controls enabled)
   */
  editable: boolean;
  /**
   * Whether or not Gizmo data is obligatory
   */
  required: boolean;

  loading: boolean;
  processing: boolean;

  // TODO: loading flag
  // dirty, touched, focused,

  /**
   * Mode in which the root gizmo was opened
   */
  mode?: 'edit' | 'show';
  /**
   * Set to true if the Gizmo is the first on the section, or the first in general
   * if no sections are included in the RootGizmo
   */
  firstInSection?: boolean;
  /**
   * Set to true if the Gizmo is the last on the section, or the first in general
   * is no sections are included in the RootGizmo
   */
  lastInSection?: boolean;

  section?: ISectionFeatures;
  navbarMenu?: INavbarMenuFeatures;
  headerMenu?: IHeaderMenuFeatures;
  help?: IHelpFeatures;
  button?: IButtonFeatures;
  idOcrField?: IIdOcrFieldFeatures;
}

interface ISectionFeatures {
  /**
   * If the section is currently active
   */
  active?: boolean;
  /**
   * The section's position
   */
  //! Is this necessary
  position?: number;
  /**
   * We're going to assign if the section is the first so we won't display the
   * back button
   */
  first?: boolean;
  /**
   * It will be assigned to a section that's not the first or the last
   */
  middle?: boolean;
  /**
   * We're going to populate this value in order to not display the next button
   */
  last?: boolean;
}

interface INavbarMenuFeatures {
  /**
   * When set to true the sub menu collapse will be open
   */
  isSubMenuOpen?: boolean;
}

interface IHeaderMenuFeatures {
  /**
   * When set to true the sub menus collapse will be shown
   */
  isSubMenusOpen?: boolean;
}

type THelpPosition = 'under_label' | 'under_gizmo';

/**
 * When a help message is configured this feature will be populated
 */
interface IHelpFeatures {
  message: string;
  type: 'tooltip' | 'text';
  position?: THelpPosition;
}

interface IButtonFeatures {
  url?: string;
  filename?: string;
}

interface IIdOcrFieldFeatures {
  onlyFront?: boolean;
  /**
   * When this feature is set to false the scanner will be disabled and the controls to upload
   * files will be shown.
   */
  scannerEnabled?: boolean;
}

/**
 * This object is passed to the update features function in order to update a
 * specific feature. We did it like this in order to avoid overriding other features
 * that weren't intended to be updated
 */
export interface ISingleFeature {
  /**
   * Complete path of the feature
   */
  path: string;
  /**
   * Value that will be set on the feature
   */
  value: string | boolean;
}

/**
 * This will tell us if the offline status of a gizmo data (filled form):
 *
 *  - notStored: it was brought from the server but still hasn't been
 *      saved to the local database (this won't be stored, it's just here
 *      for interfaces compatibility)
 *  - draft: the user has been working with the filled form but still
 *      hasn't pressed save
 *  - saved: the user has pressed save on the filled form
 *  - uploaded: the form has been stored and changes has been uploaded
 *      to the server
 *
 *  - draft (s0+s0u): new and updated filled form in draft state
 *  - newSaved (s1): new filled form is saved, it's ready to be uploaded
 *  - savedUploaded (s2): form is uploaded to the server (and hasn't been updated, because it hasn't changed)
 *  - savedUpdated (s3): form is downloaded from the server (and aligned with the latest changs)
 *  - updatedSaved (s1u): a filled form that was downloaded from the server was modified and
 *      saved. And is ready to be uploaded
 */
export type TOfflineStatus =
  | 'notStored'
  | 'saved'
  | 'uploaded'
  | 'draft' // s0 + s0u
  // | 'updatedDraft'    // s0u
  | 'newSaved' // s1
  | 'savedUploaded' // s2
  | 'savedUpdated' // s3
  | 'updatedSaved'; // s1u

/***** V2 **/
/**
 * All these interface define the data that's going to be arriving from the server which
 * will be version 2. Because it's mostly generated by the v2 admin.
 */

/**
 * Definition of a status configuration
 */
export interface IRootGizmoDataStatus {
  f_id: string;
  title: string;
  color: string;
  actions: {
    f_id: string;
    title: string;
    color: string;
    action: string;
    status: string;
  }[];
}

/**
 * Root gizmo of type form
 */
export interface IFormGizmoV2 {
  type?: 'form';
  /**
   * Database id
   */
  id: string;
  /**
   * Option object about the behaviour of the hole form
   */
  advanced_options?: {
    filledFormsIndex?: boolean;
    /**
     * String with mustache notation which can be used to configure the title
     * displayed on each of the elements
     */
    elementsTitle?: string;
    sync?: {
      /**
       * When to download gizmo data without user interaction
       *  - auto: automatically
       *  - never: never
       */
      download?: 'auto' | 'never';
      /**
       * When to delete gizmo data without user interaction
       *  - auto: automatically after it has been uploaded to the server
       *  - never: it's never deleted, only when the user does it manually
       *  - after: when the data has been uploaded to the server how much time has to pass in
       *      order to be deleted automatically
       */
      delete?: 'auto' | 'never' | 'after';
      /**
       * String defining how much time after the data root gizmo data will be deleted
       */
      deleteAfter?: string;
    };
    form?: IFormOptions;
    /**
     * When set to true the catalogs will be brought from the server only when the gizmos
     * are being rendered
     */
    onlineCatalogs?: boolean;
  };

  redirect?: {};
  conditions?: [];
  enabled?: boolean;
  /**! Remove this */
  pause?: boolean;
  public?: boolean;
  publicMode?: boolean;
  created_at?: Date;
  updated_at?: Date;
  blocks?: {};
  f_id_index?: number;
  status?: {
    f_id: string;
    title: string;
    color: string;
    actions: {
      f_id: string;
      title: string;
      color: string;
      action: string;
      status: string;
    }[];
  }[];
  variables?: [];

  /**
   * Version of the form
   */
  version: number;
  /**
   * Title or label
   */
  title: string;
  folio?: string;
  /**
   * Catalogs configurations (right now they include the options)
   */
  catalogs?: IGizmoCatalogV2[];

  /**
   * Gizmos that will be shown before the main form, they are v3 configs
   */
  preGizmos?: IGizmo[];
  /**
   * This is the configuration being produced by the Flowy 2 admin, v2 cofigurations
   */
  questions: IGizmoQuestionV2[];
  /**
   * Gizmos that will be shown after the main form has been filled, v3 configs
   */
  postGizmos?: IGizmo[];

  // /**
  //  * This key was introduced in order to provide compatibility between
  //  * the new configuration and this legacy one
  //  */
  // options?: {
  //   form?: {
  //     /**
  //      * This validation will provide the user with a modal control where they're going
  //      * to provide a biometric. It will be done on the provided stages.
  //      */
  //     biometric?: {
  //       /**
  //        * The stages where this validation will run, i.e., before saving
  //        */
  //       stages: TRootGizmoLifecycleStages[];
  //     };
  //   };
  // };
}

/**
 * Each of the options on the catalog
 */
export interface IGizmoCatalogOptionV2 {
  /**
   * Database id, right now we're not using it
   */
  _id?: string;
  id?: string;
  formId?: string;
  /**
   * This will correspond to the 'catalog' key in the question config
   */
  catalogFid?: string;
  /**
   * This is the id we're using
   */
  f_id: string;
  /**
   * Actual value of the catalog option
   */
  value: string | number;
  /**
   * If present it will show on the control instead of the value
   */
  label?: string | number;
  /**
   * This value is used by the backend, when it's false it won't be sent
   */
  enable?: boolean;

  edit?: boolean;
  /**
   * When it's an dependant option this value will be populated
   */
  parentOptionId?: string;

  version?: number;

  /**
   * In case the gizmo was configured as tristate this flag is going to be populated
   */
  checked?: boolean;

  /**
   * In case the external option was configured with metadata, it's going to be
   * stored here
   */
  answers?: {
    [qfid: string]: IGizmoDataV2;
  };
}

/**
 * This interface describe the objects arriving in the 'catalogs' key of a
 * form V2 that has 'optios' and 'options_dependant' questions
 */
export interface IGizmoCatalogV2 {
  /**
   * This key corresponds to the key 'catalog' in the question
   */
  f_id: string;
  /**
   * The following 5 fields are used by the admin v2
   */
  f_index?: number;
  online: boolean;
  TOTAL_OPTIONS: string;
  OPTIONS_PER_PAGE: string;
  currentPage: string;
  /**
   * Catalog options
   */
  options: IGizmoCatalogOptionV2[] | { [fid: string]: IGizmoCatalogOptionV2[] };
}

export interface IGizmoQuestionV2Options {
  editable?:
    | boolean
    | {
        isEditable: boolean;
        flux: string;
      };
  selectField?: {
    /**
     * Type of options this select field will be loading
     */
    optionsType: 'annex_form';
    /**
     * Uid of the annex_form field
     */
    annexFormGizmoUid: string;
    /**
     * Expression with mustaches of the fields that will be used as label
     */
    label?: string;
    value?: string;
  };
}

/**
 * Gizmo of the questions
 */
export interface IGizmoQuestionV2 {
  /**
   * Flowy id
   */
  f_id: string;
  /**
   * Unique user id
   */
  uid?: string;
  /**
   * Type of question
   */
  type:
    | 'rate'
    | 'title'
    | 'text'
    | 'number'
    | 'photo'
    | 'file'
    | 'form_embedded'
    | 'fingerprint'
    | 'date'
    | 'options'
    | 'options_dependant'
    | 'multipleOption'
    | 'phone'
    | 'subtitle'
    | 'email'
    | 'folio'
    | 'signature'
    | 'intro'
    | 'id_ocr'
    | 'jump'
    | 'location'
    | 'code_reader';
  /**
   * Title or label
   */
  title: string;
  /**
   * String used as numeration on the mobile client
   */
  number?: string;

  edit?: boolean;
  enable?: boolean;
  required?: boolean;

  subtitle?: string;
  introduction?: string;

  /**
   * If this option is set to true it means the question will be displayed on
   * the filled forms index
   */
  column?: boolean;

  /**
   * Options (select) configs
   */
  catalog?: string;
  /**
   * When present it means this select field will be listening to changes of the parent
   */
  fk?: string;
  /**
   * When set to true we're going to add an other option
   */
  other?: boolean;
  /**
   * When set we'll display this message insead of the default 'Otro' message
   */
  other_message?: string;
  /**
   * When present every time the options are set they will appear in another order
   */
  random_order?: boolean;
  control?: 'SELECT' | 'RADIOBUTTON';

  /**
   * Photo question options
   */
  defaultCamera?: 'BACK' | 'FRONT';
  inputSource?: 'BOTH' | 'CAMERA' | 'LOCAL_FILE';

  /**
   * form_embedded particular options
   */
  searchEnabled?: true;
  createEnabled?: true;
  form_id?: string;
  form_title?: string;
  /**
   * When this flag is set to true that question will be used as a title
   * in the filled forms index
   */
  use_value_as_title?: boolean;
  /**
   * When configured a help message will be shown on the gizmo
   */
  helpMessage?: string;
  /**
   * When present in a date question it will have the values:
   *  - date: DD-MM-AAAA
   *  - datetime: DD-MM-YYYY HH:MM
   *  - time: HH:MM
   */
  mode?: 'date' | 'datetime' | 'time';
  columns?: {
    f_id: string;
    number: string;
    title: string;
  }[];
  metadata?: IGizmoQuestionV2[];

  /**
   * Form embedded configuration that was supported on mobile when the mobile core was
   * developed
   */
  formEmbedded?: {
    /**
     * Minimum number of embedded forms required by the admin
     */
    min?: number;
    /**
     * Maximum number of embedded forms required by the admin
     */
    max?: number;
  };
  status?: {
    f_id: string;
    title: string;
    color: string;
    // Don't know what's this for
    indexColor?: number;
    actions: {
      f_id: string;
      title: string;
      color: string;
      action: string;
      status: string;
    }[];
  }[];
  // options?: any | IGizmoQuestionV2Options;
  // options?: IGizmoQuestionV2Options;
  options?: IGizmoOptions;

  // Rate options
  rateType?: 'yesNo' | 'face' | 'colorFace' | 'stars' | 'gender' | 'thumbs';
  howmany?: number;

  // Number options
  /**
   * Number has to be at least this value
   */
  minimum?: number;
  /**
   * Number has to be the most this value
   */
  maximum?: number;
  decimals?: boolean;
  numberOfDecimals?: number;
  thousands?: boolean;

  /**
   * When set it will be shown before the text being introduced by the user
   */
  prefix?: string;
  /**
   * When set to true the text will be changed to uppercase and remove the accents
   */
  uppercaseAnswer?: boolean;

  // Text options
  regex?: string;
  regexexplain?: string;

  // Id Ocr options
  recognizers?: {
    [recognizer: string]: {
      [idElement: string]: string;
    };
  };

  // Legacy default value configuration
  defaultValue?: {
    fid: string;
    type: 'user';
    path: string;
  }[];

  /**
   * This will be present when a jump question is configured
   */
  destinations?: {
    f_id: string;
    /**
     * We're not interested in this
     */
    condition: any;
    /**
     * If the conditional is true we're going to display from this Gizmo onwards
     */
    destination: string;
    /**
     * This string is the one we're going to transform to a Flux script
     */
    conditional: string;
    /**
     * We're not describing this because we're not using it
     */
    conditional_object: any;
  }[];
}

export interface IFilledFormV2 {
  /**
   * Database id
   */
  _id?: string;
  /**
   * Database id
   */
  id?: string;
  /**
   * Id of the form to which the filled form belongs to
   */
  formId: string;
  /**
   * Version of the form we're using to create this filled form
   */
  formVersion: number;
  /**
   * Filled form id
   */
  fid: string;
  /**
   * Version number of the filled form
   */
  version: number;
  /**
   * Filled form id, which is generated on the client
   */
  filledFormId: string;
  /**
   * Numeration string, what the user will see in the number part of the question
   */
  number?: string;

  draft?: boolean;
  /**
   * If the filled form has been uploaded to the server
   */
  uploaded?: boolean;
  updated?: boolean;

  battery_level?: number;

  /**
   * The filled form data created by the user
   */
  answers: {
    [qfid: string]: {
      [answerIndex: string]: {
        value?: any;

        /**
         * When the question type is timestamp it has also this key with
         * the epoch of the date
         */
        timestamp?: number;
        timezone?: string;

        /**
         * In case it's a question with an attachment it will have this property
         */
        attachmentId?: string;
        extension?: string;
        size?: number;
        type?: string;
        name?: string;
        ocrResult?: any;
        onlyFront?: boolean;
        source?: 'web' | 'mobile';

        // The following will appear if the answer is of a select type question,
        // because the option has all this data
        f_id?: string;
        catalogFid?: string;
        formId?: string;
        version?: number;
        enable?: boolean;
        // It will be present when the selected option has a parent
        parentOptionId?: string;

        // When a curp was scanned we're going to store here the scanned value
        scannedCurp?: string;

        answers?: any;

        // Embedded form
        filledFormId?: string;
        columns?: {
          f_id: string;
          value: string;

          number?: string;
          title?: string;
        }[];
        editable?: boolean;
        id?: string;
        _id?: string;
      };
    };
  };

  isMainForm?: boolean;
  belongsTo?: any;
  epoch_start?: number;
  epoch_end?: number;
  timezoneOffset?: number;
  source?: 'web' | 'mobile';

  userId?: string;
  username?: string;
  groupId?: string;
  createdAt?: Date;
  updatedAt?: Date;

  status?: string;
  action?: string;
  statusChanges?: any[];

  /**
   * This field will tell if the data is locally available:
   *  - notStored: it was brought from the server but still hasn't been
   *      saved to the local database
   *  - draft: the user has been working with the filled form but still
   *      hasn't pressed save
   *  - saved: the user has pressed save on the filled form
   *  - uploaded: the form has been stored and changes has been uploaded
   *      to the server
   */
  offlineStatus?: TOfflineStatus;
  // TODO: is this being used?
  associations?: {
    childFilledFormId: string;
    childFormId: string;
    parentFilledFormId: string;
    parentFormId: string;
  }[];
}

/***** END V2 **/

/***** INDEX **/
// The index is the most complicated gizmo of them all, so we're going to have multiple interfaces
// in order to define everything needed by the platform to render it correctly.
export interface IIndex {
  /**
   * Unique Flowy Id of the data
   */
  fid: string;
  label?: string;
  pagination?: {
    page: number;
    total: number;
    pageSize?: number;
    lastPage?: number;
  };
  /**
   * Create button configuration
   */
  createButton?: {
    /**
     * When set to false it won't be shown
     */
    display?: boolean;
    /**
     * Text to be shown on the button
     */
    label?: string;
  };
  columns: IIndexColumn[];
  //! We're going to try the approach of getting the cell value from the binder
  rows: any[];
  /**
   * This property will have a value when the index is being used as an annex_form index
   */
  registers?: IRootGizmoData[];
}

export interface IIndexColumn {
  fid: string;
  type?: 'operations' | 'status';
  /**
   * Column name
   */
  label: string;
  /**
   * Data path
   */
  path?: string;
  /**
   * Gizmo whose value will be shown on the column
   */
  gizmo?: IGizmo;
  operations?: IIndexOperation[];
  /**
   * In case the column is a status column we're going to have this property
   */
  status?: IRootGizmoDataStatus[];
}

export interface IIndexRow {
  id?: string;
  fid: string;
  d?: {
    [qfid: string]: IGizmoData | IGizmoData[];
  };
  // TODO: offlineStatus
}

/***** END INDEX **/

/***** FILES */
export interface IFlowyFile {
  /**
   * Flowy id of the RootGizmo
   */
  rootGizmoFid: string;
  /**
   * Flowy id of the RootGizmoData
   */
  rootGizmoDataFid: string;
  /**
   * Fid of the gizmo that's holding the file
   */
  gizmoFid: string;
  /**
   * Unique id generated for the loaded file
   */
  attachmentId: string;
  /**
   * File coming from the UI layer
   */
  file: File;
  /**
   * Set to true when the file was downloaded from the server
   */
  isFromServer?: boolean;
}
/***** END FILES */

/***** DAO */

export interface ISyncStats {
  total: {
    /**
     * s0 + s0u
     */
    draftGizmoData: number;
    /**
     * s1
     */
    newGizmoDataToUpload: number;
    /**
     * s1u
     * Gizmo data that's stored in the local database but still hasn't been
     * uploaded to the server
     */
    gizmoDataToUpload: number;
    /**
     * s2
     * Gizmo data that's stored in the local database and that has been uploaded
     */
    gizmoDataUploaded: number;
    /**
     * s3
     */
    gizmoDataDownloaded: number;
  };
  /**
   * Same stats specific to each form
   */
  perForm: {
    [formId: string]: {
      /**
       * How many data of this specific form is in the server
       *
       * 'Capturas almacenadas en servidor'
       */
      gizmoDataOnTheServer: number;

      /**
       * s0 + s0u
       * 'Capturas almacenadas en teléfono como borrador'
       */
      draftGizmoData: number;
      /**
       * s1
       * 'Capturas nuevas almacenadas en teléfono pendientes por ser actualizadas en el servidor'
       */
      newGizmoDataToUpload: number;
      /**
       * s1u
       * Stored locally but hasn't been uploaded
       * 'Capturas almacenadas en teléfono pendientes por ser actualizadas en el servidor'
       */
      gizmoDataToUpload: number;
      /**
       * s2
       * 'Capturas almacenadas en teléfono ya actualizadas en servidor'
       * Stored locally and uploaded
       */
      gizmoDataUploaded: number;
      /**
       * s3
       * 'Capturas descargadas desde el servidor sin modificar'
       */
      gizmoDataDownloaded: number;

      /**
       * s0 + s0u
       * 'Attachments ligados a gizmo data que es un borrador'
       */
      draftAttachments: number;
      /**
       * s1
       * 'Attachments ligados a filled forms nuevas y pendientes por ser actualizadas en el servidor'
       */
      newAttachmentsToUpload: number;
      /**
       * s1u
       * Stored locally but hasn't been uploaded
       */
      attachmentsToUpload: number;
      /**
       * s2
       * Stored locally and uploaded on the server
       */
      attachmentsUploaded: number;
      /**
       * s3
       */
      attachmentsDownloaded: number;
    };
  };
}
/***** END DAO */

/***** DATABASE */
/**
 * Types of data (model) that can be saved in Realm
 */
export type TRealmDataType =
  | 'attachments'
  | 'catalogOptions'
  | 'forms'
  | 'filledForms'
  | 'globals'
  | 'userEvents';

export interface IRealmFormGizmo {
  /**
   * In the case of the form the Flowy id is the same as the db id
   */
  fid: string;
  /**
   * Database id
   */
  id: string;
  /**
   * User who's saving the form gizmo
   */
  userId: string;
  /**
   * Form's version
   */
  version: number;
  /**
   * True if the form is a main form
   */
  isMain?: boolean;
  /**
   * Actual configuration
   */
  formGizmo: IFormGizmoV2;
  // formGizmo: IGizmo;
}

export interface IRealmFilledForm {
  /**
   * Flowy id (generated on the client side)
   */
  fid: string;
  /**
   * Database id
   */
  id: string;
  /**
   * User's database id
   */
  userId?: string;
  /**
   * Id of the form this filled form belongs to
   */
  formId: string;
  /**
   * Version of the form used to create this filled form
   */
  formVersion: number;
  /**
   * Filled form's version
   */
  version: number;
  /**
   * If the form is already uploaded to the server, when it has changes and wasn't
   * uploaded this flag will be false
   */
  uploaded: boolean;
  draft: boolean;
  updated: boolean;

  /**
   * Date of the last time this filled form was saved (edited) on the client side
   */
  savedAt: number;
  /**
   * This field will tell if the data is locally available:
   *  - notStored: it was brought from the server but still hasn't been
   *      saved to the local database (this won't be stored, it's just here
   *      for interfaces compatibility)
   *  - draft: the user has been working with the filled form but still
   *      hasn't pressed save
   *  - saved: the user has pressed save on the filled form
   *  - uploaded: the form has been stored and changes has been uploaded
   *      to the server
   */
  offlineStatus: TOfflineStatus;
  /**
   * Actual filled form
   */
  // gizmoData: IRootGizmoData;
  gizmoData: IFilledFormV2;
}

export interface IRealmAttachment {
  fid: string;
  /**
   * Path of the folder where the file is located
   */
  path: string;
  /**
   * Name of the file with extension
   */
  file: string;
  version?: number;
  /**
   * Database id of the form that's referencing this attachment
   */
  formId: string;
  /**
   * Filled form id of the filled form referencing this attachment
   */
  filledFormId?: string;
  /**
   * When the file is an attachment for an answer of a question, in here we'll save
   * the question's 'f_id', example, 'q0'
   */
  questionFid?: string;
  /**
   * The Flowy format supports multiple answers in one object, this is the index
   */
  answerIndex?: number;

  /**
   * Flag that indicates if the attachment has been uploaded to the server
   */
  uploaded: boolean;
  draft: boolean;
  updated: boolean;

  /**
   * Name says it all
   */
  offlineStatus: TOfflineStatus;
}

export type TUserEventCategory =
  | 'session'
  | 'filledForm'
  | 'general'
  | 'data'
  | 'gizmo';

export type TUserEventType =
  // session
  | 'appOpen'
  | 'loggedIn'
  | 'loggedOut'
  // filledForm
  | 'createFilledForm'
  | 'closeFilledForm'
  | 'editFilledForm'
  | 'startSaveFilledForm'
  | 'finishSaveFilledForm'
  | 'deleteFilledForms'
  | 'downloadFilledForms'
  // data
  | 'clearData'
  | 'exportData'
  // general
  | 'sync'
  | 'idOcr';

export interface IRealmUserEvent {
  /**
   * Flowy generated unique id
   */
  fid?: string;
  /**
   * User event category
   */
  category: TUserEventCategory;
  /**
   * Type of user event
   */
  type: TUserEventType;
  /**
   * User id
   */
  userId: string;
  /**
   * UTC timestamp when the event happened
   */
  timestamp: number;
  /**
   * Timezone in string format
   */
  timezone: string;
  /**
   * Set to true when the info has been sent to the server
   */
  uploaded: boolean;
  /**
   * This object can be used by each of the types of events to transmit
   * extra data to the server
   */
  data?: any;
}

/**
 * Interface describing the document of a global, this are documents with
 * information which will be saved only once.
 *
 * The first example is globalStats
 */
export interface IRealmGlobal {
  /**
   * Type of global data
   */
  fid: 'globalStats';
  /**
   * Actual global data
   */
  data: ISyncStats;
}

export interface IRealmCatalogOption {
  /**
   * Unique Flowy ID
   */
  fid: string;
  /**
   * What will be shown on the control
   */
  value: string | number;
  /**
   * Object with extra data which could be later used by the Gizmo for logic and
   * or evaluation purposes
   */
  extraData?: any;
  /**
   * In case the option has been selected
   * ? Should we have a 'only local' or should we go all the way to the server?
   */
  selected?: boolean;
  /**
   * In case it's an options_dependant or filtered_options with multiple levels
   */
  parentFid?: string;
  /**
   * Catalog group Flowy id
   */
  catalogFid: string;
  //? Should we put this
  //? parentCatalogFid?: string;
}

/***** END DATABASE */

/***** USER */

/**
 * Interface describing the object of user credentials
 */
export interface IUserCredentials {
  'access-token'?: string;
  /**
   * Flowy id of the form used to authenticate the user
   */
  'auth-form-fid'?: string;
  /**
   * Key where the token generated by the Auth libraries will be stored
   */
  Authorization?: string;
  client?: string;
  'token-type'?: string;
  uid?: string;
  expiry?: string;
  /**
   * In case it's an API user this key will be specified
   */
  'api-key'?: string;
}

/**
 * Interface desribing the user object
 */
export interface IUserV2 {
  id: string;
  username: string;
  role?: string;
  email?: string;
  /**
   * User credentials
   */
  credentials?: IUserCredentials;
  gizmos?: IGizmo;
  /**
   * We're using variables because it's how its stored on the user
   */
  variables?: {
    [qfid: string]: IGizmoData | IGizmoData[];
  };
}
/***** END USER */

/**** NOTIFICATIONS */
/**
 * Interfae describing the Notification object
 */
export interface IFlowyNotification {
  // Should we add this props????
  // autoDismiss
  // position

  /**
   * Unique id for the element
   */
  fid: string;
  /**
   * Type of notification
   */
  type:
    | 'success'
    | 'warning'
    | 'error'
    // | 'errorLight'
    // | 'warningLight'
    // | 'successLight'
    | 'flowy'
    | 'user';
  /**
   * Mesage that will be shown on the notification
   */
  label: string;
  /**
   * After this time the toast will disappear
   */
  dismissTime?: number;
}
/**** END NOTIFICATIONS */

/***** PROVIDED BY THE PLATFORM */

/**
 * Functions used by the core to access platform funcionality, this object of functions can't be
 * in the IPlatformInterface because in order to access this functionality custom hook have to be
 * used in the platform layer.
 */
export interface IPlatformCallbacks {
  /**
   * Call provided platform operation callback
   * @param {TPlatformOperation} operation platform operation
   * @param {object} params parameters being passed to the platform callback
   */
  platformOperationCB(props: {
    // operation: TPlatformOperation;
    operation: TOperations;
    params: {
      rootGizmoFid?: string;
      rootGizmoDataFid?: string;
      // This variable is used when the operation is navigation
      path?: string;
    };
  }): Promise<any>;
}

/**
 * Interface that has to be implemented on the Platform in order for the core
 * to complete many of it functionalities
 */
export interface IPlatformInterface {
  /**
   * It will be called by FlowyGlobal during it's initialization
   */
  init(): Promise<void>;
  /**
   * Returns true or false depending if the client is authenticated
   */
  isAuthenticated(): Promise<boolean>;
  // authError(): Promise<void>;
  /**
   * TODO: We have to define the params that will be sent
   */
  // analyticsEvent(): Promise<void>;
  /**
   * Function used by the core to send the user to the UI layer, in order for the
   * UI layer to store it and use it when needed
   *
   * @param {IUserV2} user the user object being set by the core
   */
  setUser(user: IUserV2): Promise<void>;
  /**
   * Return the authenticated user
   */
  getUser(): Promise<IUserV2>;
  /**
   * It will be called by the core in order to ask the Platform layer to logout of
   * the user's session
   */
  logout(reason?: string): Promise<void>;
  /**
   * Get the user credentials, it's on a separate method because some authentication
   * libraries/methods dinamically change the credentials. So we have to ask them
   * every time we're going to interact with the server
   */
  getUserCredentials(): Promise<IUserCredentials>;

  /**
   * Save a file in the platform's local storage
   */
  fileSet(props: {
    path: string;
    fileFid: string;
    fileName?: string;
    fileExtension: string;
    base64file?: string;
    file?: File;
  }): Promise<void>;
  /**
   * Get a file from the platform's local storage
   */
  fileGet(props: {
    // attachmentId?: string;
    path: string;
    fileFid: string;
    fileExtension: string;
    // }): Promise<{ source: string } | undefined>;
  }): Promise<{ file: File } | undefined>;
  /**
   * Remove a file from the platform's local storage
   */
  fileRemove(props: { path: string; fileFid: string }): Promise<void>;
  /**
   * Check if a file exists in the platform's local storage
   */
  fileExists(props: {
    // attachmentId?: string;
    path: string;
    fileFid: string;
  }): Promise<boolean>;
  //? I think this two won't be necessary
  // fileDownload(props: {
  //   path: string;
  //   fileFid: string;
  //   // attachmentId: string;
  // }): Promise<void>;
  // fileUpload(props: {
  //   // attachmentId: string;
  //   path: string;
  //   fileFid: string;
  // }): Promise<void>;
}
/***** END PLATFORM */

/*********** INTERFACES USED INTERNALLY */

export interface ISectionState {
  /**
   * Flowy id of the section linked to the state
   */
  sectionFid: string;
  /**
   * Section gizmo instance
   */
  gizmo: Gizmo | RootGizmo;
  /**
   * List of transitions that indicate to where the state can move
   */
  transitions: ISectionTransition[];
  /**
   * Depending on flux evaluations this flag is going to change, when set to true
   * the gizmos on that section are going to be displayed on the UI layer
   */
  display: boolean;
}

export interface ISectionTransition {
  origin: string;
  destiny: string;
  flux?: string;
  fluxResult?: boolean;
}

export interface ISectionStateMachine {}

/**
 * This interface will be used to have the Gizmo ancestors path
 */
export interface IGizmoAncestor {
  /**
   * Flowy Id of the Gizmo
   */
  fid: string;
  /**
   * User Id of the Gizmo
   */
  uid?: string;
}

/**
 * Structure to map uid (user defined unique ids) to fids (auto generated Flowy Ids)
 */
export interface IUidToFid {
  /**
   * Key is uid and value if fid
   */
  [uid: string]: string;
}

/**
 * This interface models a requet to get catalog data from the server
 */
export interface IOnlineCatalogRequest {
  /**
   * Flowy Id of the gizmo who's using the catalog
   */
  gizmoFid: string;
  /**
   * Flowy Id of the catalog
   */
  catalogFid: string;
  /**
   * Flowy Id of the parent catalog
   */
  parentCatalogFid?: string;
  /**
   * Flowy Id of the parent option, it's used to build the catalog
   */
  parentOptionFid?: string;
}

/***** EVENTS */

/**
 * Events that will be sent through the observers
 */
export interface IGizmoEvent {
  /**
   * Supported types of event
   *  - gizmo.operation: operation originating from a Gizmo
   *  - root-gizmo.operation: operation originating from a RootGizmo
   *  - gizmo.data: event carrying Gizmo data
   *  - gizmo.set-config: it will be sent in order to notify a gizmo that it's config has changed
   *  - platform-operation: event originated from a platform operation
   */
  type:
    | 'gizmo.operation'
    | 'root-gizmo.operation'
    | 'gizmo.data'
    | 'gizmo.set-data'
    | 'gizmo.clear-data'
    | 'gizmo.set-config'
    | 'platform-operation';
  /**
   * Element that originated the event
   */
  src: {
    /**
     * Type of element originating the gizmo event
     */
    type: 'root-gizmo' | 'gizmo' | 'user' | 'flowy-foundation';
    /**
     * The Flowy id of the RootGizmo who's originating the event
     */
    rootGizmoFid?: string;
    /**
     * Flowy id of the Gizmo originating the event
     */
    gizmoFid?: string;
    /**
     * Unique id of the originating Gizmo
     */
    gizmoUid?: string;
  };
  /**
   * If the event is directed to a particular element this will be populated
   */
  dest?: {
    type: 'gizmo';
    /**
     * Flowy id of the destination root gizmo
     */
    rootGizmoFid?: string;
    /**
     * Flowy id of the destination Gizmo
     */
    gizmoFid?: string;
    /**
     * User id of the destination Gizmo
     */
    gizmoUid?: string;
  };
  /**
   * Event's data
   */
  data?: {
    /**
     * Data from the gizmo operation GizmoEvent
     */
    gizmoOperation?: {
      /**
       * Gizmo operation being saved
       */
      operation: TGizmoOperation;
    };
    rootGizmoOperation?: {
      /**
       * When a root gizmo does an operation it will notify about it, there are multiple reasons
       * for this:
       *  - form.lyfecycle.after-submit: when the parent form is submitted
       *  - form.clear-processing: clear processing flags in the root gizmo
       *  - child.form.lyfecycle.after-submit: when a child form has been saved
       *  - child.form.lyfecyle.delete: when a child form has been deleted
       *  - child.form.lyfecycle.cancel: when a child form has been cancelled
       */
      type:
        | 'form.lifecycle.after-submit'
        | 'form.clear-processing'
        | 'child.form.lyfecycle.submit'
        | 'child.form.lyfecycle.after-submit'
        | 'child.form.lyfecycle.delete'
        | 'child.form.lyfecycle.cancel';
      /**
       * Flowy id of the root gizmo data
       */
      dataFid?: string;
      /**
       * Database id of the root gizmo data
       */
      dataId?: string;
      /**
       * RootGizmoData being saved
       */
      rootGizmoData?: IRootGizmoData;
    };
    /**
     * Data from the platform operation GizmoEvent
     */
    platformOperation?: {
      /**
       * Platform operation being sent
       */
      operation: TPlatformOperation | TGizmoOperation;
      /**
       * When we're sending section operations we need to send the Gizmo ancestors
       */
      ancestors?: IGizmoAncestor[];
      /**
       * When we're sending a PlatformOperation from the form validations UI this
       * will be provided
       */
      // validationErrors?: IValidationError[];
      /**
       * If a platform operation of type 'keyboard.key' is being send in here
       * we're going to put the key
       */
      key?: 'enter';
    };
    /**
     * Data that's going to be set on the destination Gizmo
     */
    gizmoSetData?: {
      data?: IGizmoData | IGizmoData[];
      value?: string;
      /**
       * This is the RootGizmoConfig of the result RootGizmoData
       */
      rootGizmoConfig?: IGizmo;
      rootGizmoData?: IRootGizmoData;
      /**
       * Destiny configuration, it has the origin fid or uid from the found register,
       * and also the destiny fid or uid in the actual RootGizmo
       */
      destiny?: any;
    };
    gizmoSetConfig?: {
      /**
       * Types of gizmoSetConfig events.
       *  - update-catalog: when a catalog has been updated
       */
      type: 'update-catalog';
    };
    gizmoData?: {
      /**
       * Types of gizmoData events
       * - parent-init-update-catalog: when a parent catalog has been updated during initialization it's
       *    going to sent this event.
       */
      type: 'parent-init-update-catalog';
    };
  };
}

/***** SUSBSCRIPTIONS */
/**
 * This interface describes which events/data this Lgx or Gizmo instance is
 * interested in and will process them when a Gizmo Event arrives
 */
export interface ISubscription {
  /**
   * Type of info
   */
  type: 'gizmoData' | 'lgxResult' | 'user';
  // /**
  //  * In case we're interested on an Lgx's result or a Gizmo's data specify their
  //  * Flowy id
  //  */
  fid?: string;
  /**
   * Unique ID
   */
  uid?: string;
  /**
   * In case it's Gizmo data we specifiy the path
   */
  path?: string;
  // /**
  //  * In case it's Lgx result it will arrive here
  //  */
  // result?: boolean;
  // /**
  //  * This information is set by the Gizmo or Lgx so it can be reference by themselves
  //  * when an event arrives in response to the ISubscription this Gizmo or Lgx sent to
  //  * the RootGizmo
  //  */
  // self?: {
  //   /**
  //    * This type is used by the Gizmo to identify "why" they're listening to the GizmoEvent
  //    */
  //   type: 'labelInterpolation';
  // };
}

/**
 * TODO: should this be previously defined?
 */
export type TErrorCode =
  | 'common.required'
  | 'custom'
  | 'text_field.regex'
  | 'text_field.regex.email'
  | 'text_field.regex.rfc'
  | 'text_field.regex.curp'
  | 'text_field.password'
  | 'text_field.max-length'
  | 'text_field.min-length'
  | 'number_field.minimum'
  | 'number_field.maximum'
  | 'number_field.negative'
  | 'phone_field.format'
  | 'phone_field.verification'
  | 'annex_form.min'
  | 'annex_form.max'
  | 'file_field.server-processing'
  | 'local-validation'
  | 'server-validattion'
  | 'search.status'
  | 'search.results';

// TODO: change it to IValidationError
export interface IError {
  /**
   * Flowy id of the id generated by the core
   */
  fid: string;
  /**
   * Validation error code
   */
  code?: TErrorCode;
  /**
   * Message that can be shown to the user, it's a hummane readable message
   */
  message?: string;
  /**
   * Gizmo features, we're going to use it to know if a gizmo is being displayed
   */
  features?: IGizmoFeatures;
  /**
   * Gizmo ancestors, we're using this to know if their ancestors are being displayed (basically
   * if a section is hidden or not)
   */
  ancestors?: IGizmoAncestor[];
}

/**
 * This is the interface which will be used in all the errors of the core, this errors
 * will be sent all the way to the platform layer
 */
export interface IFlowyError {
  /**
   * Where the error was originated
   */
  origin: 'database' | 'synchronizer' | 'integration' | 'gizmo' | 'dao';
  /**
   * In case we need/want, use a code
   */
  code?:
    | 'user.not-auth'
    | 'db.not-found'
    | 'integration.not-configured'
    | 'dao.network-error'
    | 'dao.backend-error'
    | 'gizmo.init';
  /**
   * If necessary the core can put more data in this key
   */
  data?: any;
  /**
   * An explicit message (in spanish) about what is happening
   */
  message: string;
}

/*** COMM */
// Flowy has 2 ways of communicating:
//  - handlers and callbacks
//  - events

/**
 * HANDLERS the child Gizmo will send to the RootGizmo1
 */
export interface IGizmoHandlers {
  //* Root Gizmo will call this handlers to validate the data fulfills the validations
  validationsHDLR: TValidationsHDLR;
}

/**
 * It's used by the RootGizmo in order indicate the child Gizmos to run their
 * validation sequence
 */
//! TODO: set the errors interface
export type TValidationsHDLR = () => Promise<IError[]>;

/**
 * Object with all child Gizmo handlers, it's used in the RootGizmo
 */
export interface IChildGizmoHandlers {
  /**
   * Unique Flowy id as a key, and the Gizmo handlers as a value
   */
  [ufid: string]: IGizmoHandlers;
}

/**
 * CALLBACKS Mechanism used by the Gizmos in order to communicate with
 * with the RootGizmo
 */
export interface IGizmoCallbacks {
  /**
   * The Gizmo will call this function in order to register the handlers
   * with the Root Gizmo
   */
  setHandlersCB: TSetHandlersCB;
  /**
   * With these function child gizmos can access information from the root gizmo
   */
  getInfoCB: TGetInfoCB;
  /**
   * Callback used to update the Root Gizmo Data object
   */
  setGizmoDataCB: TSetGizmoDataCB;
  /**
   * Send the subscriptions to the Root Gizmo
   */
  setSubscriptionsCB: TSetSubscriptionsCB;
  /**
   * Send and event to the Root Gizmo
   */
  sendEventCB: TSendEventCB;
}

/**
 * Callback used by the child gizmo in order to register the handlers in the
 * root gizmo
 * @param {IGizmoHandlers} gizmoHandlers object containing the handler functions
 * @param {string} fid Flowy id of the child Gizmo
 * @param {string} ugid unique generated id, right now it's the same as fid, this was
 *  introduced if we need to deal with lists of Gizmos
 * @param {sring} uid Unique id provided by the user
 */
export type TSetHandlersCB = (props: {
  gizmoHandlers: IGizmoHandlers;
  fid: string;
  ugid: string;
  uid?: string;
}) => void;

/**
 * Callback used by child gizmos to acces information from the root gizmo
 * @param {string} type type of value we want to get from the root gizmo, options:
 *    - param: a param value that has arrived form the UI layer
 *    - data: a data value from the main data object
 *    - rootGizmoFid: id of the root Gizmo
 * @param {string} rootGizmoType type of root gizmo from which we're going to ask info
 *  ! I think this one has to go
 * @param {string} path Path in the params object of the parameter whose value we want
 * @param {string} fid If we're asking for a data value in here he specify the Flowy id we want
 */
export type TGetInfoCB = (props: {
  /**
   * Type of value we want to get from the root gizmo
   *  - param: a param value that has arrived form the UI layer
   *  - data: a data value from the main data object
   *  - rootGizmoFid: id of the root Gizmo
   *  - rootGizmoDataFromGlobalCache: get root gizmo data that's stored on the global cache
   *  - rootGizmoDataStatus: status of the RootGizmoData object
   *  - rootGizmoDataCreate: it will be set to true when the root gizmo data is first created
   *  - uidToFid: get the Flowy id of the user defined id
   *  - catalog: get a catalog defined in the RootGizmo configuration
   *  - gizmo: instance of a particular Gizmo
   */
  type:
    | 'param'
    | 'data'
    | 'rootGizmo'
    | 'rootGizmoFid'
    | 'rootGizmoConfig'
    | 'rootGizmoData'
    | 'rootGizmoDataFid'
    | 'rootGizmoDataFromGlobalCache'
    | 'rootGizmoDataStatus'
    | 'rootGizmoDataCreate'
    | 'uidToFid'
    | 'catalog'
    | 'catalogOnline'
    | 'gizmo';
  rootGizmoType?: 'view' | 'form';
  /**
   * Path in the params object of the parameter whose value we want
   */
  path?: string;
  /**
   * If we're asking for a data value in here he specify the Flowy id we want
   */
  fid?: string;
  /**
   * When asking for data if we provide the uid root gizmo will provied the
   * correct gizmo data
   */
  uid?: string;
  /**
   * Fid of the catalog we're asking for
   */
  catalogFid?: string;
  /**
   * Fid of the parent catalog
   */
  parentCatalogFid?: string;
  /**
   * When necessary this key will be populated with the data we need to send
   */
  data?: any;
}) => any | undefined;

/**
 * Child gizmos use this callback to update the RootGizmo data object
 */
export type TSetGizmoDataCB = (props: {
  /**
   * The Gizmo's data object
   */
  data?: IGizmoData | IGizmoData[];
  /**
   * Flowy id of the Gizmo
   */
  fid: string;
  /**
   * Unique id of the Gizmo
   */
  uid?: string;
}) => Promise<void>;

/**
 * Callback used ot set the Lgx or Gizmo subscriptions on the RootGizmo
 */
export type TSetSubscriptionsCB = (props: {
  /**
   * List of subscriptions
   */
  subscriptions: ISubscription[];
}) => void;

/**
 * Callback used to send GizmoEvents to the RootGizmo
 */
export type TSendEventCB = (props: { event: IGizmoEvent }) => Promise<void>;

/**
 * CALLBACKS Mechanism used by the RootGizmo in order to communicate with
 * with the Foundation Layer
 */
export interface IRootGizmoCallbacks {
  /**
   * Called when the user has submitted the form...
   * ! and it's valid???
   */
  submitRootGizmoDataCB: TSubmitRootGizmoData;
  /**
   * Used by the RootGizmo to send events to the global event bus
   */
  sendGlobalEventCB: TSendGlobalEventCB;
}

export type TSubmitRootGizmoData = (props: {
  rootGizmoData: IRootGizmoData;
  jsonData?: TJsonData;
  //! Should we add this flag?
  // valid: boolean;
  onSubmitFinish: () => void;
}) => Promise<void>;

export type TSendGlobalEventCB = (props: {
  event: IGizmoEvent;
}) => Promise<void>;

export type TOnSubmit = (props: {
  rootGizmoData: IRootGizmoData;
  onSubmitFinish: () => void;
}) => Promise<void>;

/**
 * Type for the function that will be provided by the by the Custom Gizmo where it
 * will run their validations
 */
export type TCustomValidations = (props: {
  gizmoData?: IGizmoData | IGizmoData[];
}) => Promise<IError[]>;

/*** OPERATIONS */

/**
 * Operations the core can ask the platform to do
 *
 *  - form.save: operation which will command the core to start the saving routine
 *  - section.next: move to the next section
 *  - section.back: move to the previous section
 *  - section.jump: move to a particular section
 */
export type TPlatformOperation =
  | 'form.save'
  | 'section.jump'
  | 'section.next'
  | 'section.back';

/**
 * Operations the core can will dispatch internally between gizmos and root gizmos
 */
export type TGizmoOperation =
  | 'form.submit'
  | 'form.cancel'
  | 'export.download'
  | 'pdf.download'
  | 'navigation.link'
  | 'navigation.back'
  | 'section.jump'
  | 'section.next'
  | 'section.back'
  | 'keyboard.key'
  | 'keyboard.shortcut'
  | 'auth.logout'
  | 'download';

export type TOperations = TPlatformOperation | TGizmoOperation;

/**
 * This interface is consumed by the UI in order to display
 * to the user results from a search query
 */
export interface ISearchResult {
  id: string;
  /**
   * This will be shown on the select control
   */
  value: string;

  /**
   * This values will be populated when the search result comes from the Flowy server
   */
  /**
   * Filled form id
   */
  f_id?: string;
  /**
   * Id of the form this result was found at
   */
  formId?: string;
  /**
   * Filled form's version
   */
  version?: number;
  status?: string;
  /**
   * If metadata was configured on the search, answers will be populated in Flowy format
   */
  answers?: {
    [qfid: string]: IGizmoData;
  };
}

/**
 * Features that will be communicated to the UI in order for the user to interact with the
 * Gizmos search funcionality
 */
export interface ISearchFeatures {
  /**
   * Set to true when the search is currently being processed
   */
  processing: boolean;
  /**
   * Message that will be displayed to the user
   */
  message?: string;
  /**
   * Type of message that will be displayed to the user
   */
  messageType?: 'error' | 'warning' | 'info' | 'success';
  /**
   * Search results that will be displayed on the select control in the UI
   */
  results?: ISearchResult[];
}

/*** UI INTERFACES */

/**
 * Object containing the configured columns for the 4 defined sizes
 */
export type TScreenColumns = {
  xl: 1 | 2 | 3 | 4 | 5 | 6;
  l: 1 | 2 | 3 | 4;
  m: 1 | 2 | 3 | 4;
  s: 1 | 2;
};

/**
 * Screen sizes in pixel
 */
export const SCREEN_SIZES = {
  small: '769px', // Mobile
  medium: '960px', // Tablet, Small Desktop
  large: '1279px', // Desktop
  extraLarge: '1280px', // Desktop large screen
  // small: 769, // Mobile
  // medium: 960, // Tablet, Small Desktop
  // large: 1279, // Desktop
  // extraLarge: 1280, // Desktop large screen
};
